/**
 * Solar panel presets — UK-available commercial-grade monocrystalline
 * modules suitable for a flat-roof C&I install at Dalsholm.
 *
 * Each entry supplies the 5 SolarParams fields used by the simulator
 * plus display-only metadata: rated kWp at the default 40-panel count,
 * module efficiency, an indicative installed price range for the 40-panel
 * array, and a notes blurb.
 *
 * Prices are ALL-IN INSTALLED for a 40-panel system in 2026 UK market
 * conditions (panels + string inverter + mounting + cabling + DNO/G99
 * application + commissioning, excl VAT). Treat them as "roughly"
 * figures for simple payback comparison, not quotes.
 *
 * The default performance_ratio of 0.85 bundles wiring/inverter/soiling/
 * mismatch losses AND the GHI→POA uplift from a 35° south tilt at
 * Glasgow's 55.9°N latitude. It is applied directly to GHI in v1;
 * proper POA transposition is a future enhancement.
 */

export const SOLAR_PRESETS = [
  {
    id: 'jinko-tiger-neo-625',
    name: 'Jinko Tiger Neo 625W',
    subtitle: '25.0 kWp @ 40 panels, N-type TOPCon bifacial',
    rated_kwp_at_40: 25.0,
    chemistry: 'N-type TOPCon',
    efficiency: 0.231,
    price_gbp_min: 21000,
    price_gbp_max: 26000,
    notes: 'Tier-1 workhorse. 30-yr linear power warranty. Large-format 2382×1134mm — check roof mounting clearance.',
    params: {
      panel_watts: 625,
      num_panels: 40,
      tilt_deg: 35,
      azimuth_deg: 0,
      performance_ratio: 0.85,
    },
  },
  {
    id: 'ja-solar-jam72d40-580',
    name: 'JA Solar JAM72D40 580W',
    subtitle: '23.2 kWp @ 40 panels, N-type bifacial',
    rated_kwp_at_40: 23.2,
    chemistry: 'N-type TOPCon',
    efficiency: 0.224,
    price_gbp_min: 20000,
    price_gbp_max: 25000,
    notes: 'Popular UK commercial choice. Bifacial glass-glass. 25-yr product warranty.',
    params: {
      panel_watts: 580,
      num_panels: 40,
      tilt_deg: 35,
      azimuth_deg: 0,
      performance_ratio: 0.85,
    },
  },
  {
    id: 'canadian-solar-topbihiku7-605',
    name: 'Canadian Solar TOPBiHiKu7 605W',
    subtitle: '24.2 kWp @ 40 panels, N-type TOPCon',
    rated_kwp_at_40: 24.2,
    chemistry: 'N-type TOPCon',
    efficiency: 0.227,
    price_gbp_min: 20500,
    price_gbp_max: 25500,
    notes: 'Larger format HiKu7 utility-scale panel adapted for C&I. Strong low-irradiance performance.',
    params: {
      panel_watts: 605,
      num_panels: 40,
      tilt_deg: 35,
      azimuth_deg: 0,
      performance_ratio: 0.85,
    },
  },
  {
    id: 'longi-himo-7-580',
    name: 'Longi Hi-MO 7 580W',
    subtitle: '23.2 kWp @ 40 panels, HPBC back-contact',
    rated_kwp_at_40: 23.2,
    chemistry: 'HPBC',
    efficiency: 0.224,
    price_gbp_min: 20500,
    price_gbp_max: 25500,
    notes: 'Back-contact cell (no front busbars) gives marginal low-light uplift. 25-yr product / 30-yr performance.',
    params: {
      panel_watts: 580,
      num_panels: 40,
      tilt_deg: 35,
      azimuth_deg: 0,
      performance_ratio: 0.85,
    },
  },
  {
    id: 'trina-vertex-s-plus-440',
    name: 'Trina Vertex S+ 440W',
    subtitle: '17.6 kWp @ 40 panels, compact N-type',
    rated_kwp_at_40: 17.6,
    chemistry: 'N-type TOPCon',
    efficiency: 0.221,
    price_gbp_min: 16000,
    price_gbp_max: 20000,
    notes: 'Smaller 1762×1134mm module — useful if roof layout is constrained. Lower total kWp at 40 panels.',
    params: {
      panel_watts: 440,
      num_panels: 40,
      tilt_deg: 35,
      azimuth_deg: 0,
      performance_ratio: 0.85,
    },
  },
  {
    id: 'qcells-qpeak-duo-ml-g11-415',
    name: 'Q.CELLS Q.PEAK DUO ML-G11+ 415W',
    subtitle: '16.6 kWp @ 40 panels, German-engineered mono',
    rated_kwp_at_40: 16.6,
    chemistry: 'Q.ANTUM mono-PERC',
    efficiency: 0.213,
    price_gbp_min: 17000,
    price_gbp_max: 21000,
    notes: 'Premium brand (Hanwha Q CELLS). Highest UK reseller markup of the six. 25-yr product warranty.',
    params: {
      panel_watts: 415,
      num_panels: 40,
      tilt_deg: 35,
      azimuth_deg: 0,
      performance_ratio: 0.85,
    },
  },
]

/** Preset entry used when the user has manually tweaked params off of a preset. */
export const CUSTOM_SOLAR_PRESET_ID = 'custom'

/** Hard UI cap on num_panels — matches available roof area at Dalsholm. */
export const MAX_PANELS = 40

/**
 * Find the preset whose params match the supplied SolarParams exactly.
 * num_panels is EXCLUDED from the match — changing only the panel count
 * should not demote a preset to "Custom".
 * Returns the preset id, or CUSTOM_SOLAR_PRESET_ID if no match.
 * Uses a 1e-6 tolerance to survive float round-trips.
 */
export function identifySolarPreset(solar) {
  if (!solar) return CUSTOM_SOLAR_PRESET_ID
  const keys = ['panel_watts', 'tilt_deg', 'azimuth_deg', 'performance_ratio']
  for (const preset of SOLAR_PRESETS) {
    const match = keys.every(k => Math.abs((solar[k] ?? NaN) - preset.params[k]) < 1e-6)
    if (match) return preset.id
  }
  return CUSTOM_SOLAR_PRESET_ID
}

/** Midpoint of the preset's installed-price range, used for simple-payback display */
export function presetMidPriceAt(preset, numPanels = 40) {
  if (!preset) return null
  const mid = (preset.price_gbp_min + preset.price_gbp_max) / 2
  // Scale linearly with panel count from the 40-panel base price.
  return mid * (numPanels / 40)
}

/** Look up a solar preset by id; returns undefined for 'custom' */
export function getSolarPreset(id) {
  return SOLAR_PRESETS.find(p => p.id === id)
}

/** Compute rated kWp for a given panel_watts + num_panels combo */
export function ratedKwp(panelWatts, numPanels) {
  return (panelWatts * numPanels) / 1000
}
